import React, { useCallback, useEffect, useState } from "react";
import ReserveReportSummary from "./ReserveReportSummary";
import {
  Calendar,
  Clock,
  DollarSign,
  Printer,
  Receipt,
  RefreshCw,
  Terminal,
  UserCircle,
  Users,
} from "lucide-react";
import XReportModal from "./XReportModal";
import { buildZReportPrintHtml } from "../shared/zReportPrint";

const API_BASE = "/api";
const formatCurrency = (value = 0) =>
  `${new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value) || 0)} FCFA`;
const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return value;
  }
};
const formatSignedCurrency = (value = 0) => {
  const amount = Number(value) || 0;
  if (amount > 0) return `+${formatCurrency(amount)}`;
  if (amount < 0) return `-${formatCurrency(Math.abs(amount))}`;
  return formatCurrency(0);
};
const formatNumber = (value = 0) =>
  new Intl.NumberFormat("fr-FR").format(Number(value) || 0);

const formatReportRole = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  const labels = {
    admin: "Admin",
    cashier: "Caissier",
    caissier: "Caissier",
    manager: "Manager",
    server: "Serveur",
    serveur: "Serveur",
    waiter: "Serveur",
    superadmin: "Super Admin",
  };
  return labels[normalized] || (normalized
    ? `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`
    : "Équipe");
};

const cardBg = (isDark) =>
  isDark ? "bg-[#0f172a]/80 border border-white/10" : "bg-white border border-slate-100";

const CashReports = () => {
  const todayDate = new Date().toISOString().split("T")[0];

  const [xStartDate, setXStartDate] = useState(todayDate);
  const [xEndDate, setXEndDate] = useState(todayDate);
  const [xCashierId, setXCashierId] = useState("");
  const [cashSessions, setCashSessions] = useState({
    xSessions: [],
    closedSessions: [],
    stats: null,
    cashiers: [],
  });
  const [logoutAudits, setLogoutAudits] = useState([]);
  const [zReport, setZReport] = useState(null);
  const [showXReport, setShowXReport] = useState(false);
  const [selectedXSession, setSelectedXSession] = useState(null);

  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingLogoutAudits, setLoadingLogoutAudits] = useState(true);
  const [loadingZReport, setLoadingZReport] = useState(true);
  const [sessionsError, setSessionsError] = useState(null);
  const [logoutAuditsError, setLogoutAuditsError] = useState(null);
  const [zReportError, setZReportError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [currentUser] = useState(() => {
    try {
      const superUser = localStorage.getItem("moo_user_superadmin");
      if (superUser) return JSON.parse(superUser);
      const user = localStorage.getItem("moo_user");
      return user ? JSON.parse(user) : null;
    } catch (_e) {
      return null;
    }
  });

  const getPreferredDark = () => {
    if (typeof document === "undefined") return false;
    const docHasDark = document.documentElement.classList.contains("dark");
    const stored = typeof localStorage !== "undefined" ? localStorage.getItem("theme") : null;
    const media = typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
      : false;
    return stored ? stored === "dark" : docHasDark || media;
  };
  const [isDarkMode, setIsDarkMode] = useState(getPreferredDark);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const target = document.documentElement;
    const syncTheme = () => setIsDarkMode(getPreferredDark());
    const observer = new MutationObserver(syncTheme);
    observer.observe(target, { attributes: true, attributeFilter: ["class"] });
    syncTheme();
    const media = window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)") : null;
    media?.addEventListener("change", syncTheme);
    return () => {
      observer.disconnect();
      media?.removeEventListener("change", syncTheme);
    };
  }, []);

  const fetchCashSessions = useCallback(async () => {
    try {
      setLoadingSessions(true);
      setSessionsError(null);
      const paramsObject = {
        startDate: xStartDate,
        endDate: xEndDate,
        period: "90",
      };
      if (xCashierId) {
        paramsObject.cashierId = xCashierId;
      }
      const params = new URLSearchParams(paramsObject);
      const response = await fetch(`${API_BASE}/reports/cash-sessions?${params}`);
      if (!response.ok) throw new Error("Impossible de charger les rapports X/Z");
      const data = await response.json();
      setCashSessions({
        xSessions: data.xSessions || data.openSessions || [],
        closedSessions: data.closedSessions || [],
        stats: data.stats || null,
        cashiers: data.cashiers || [],
      });
    } catch (error) {
      console.error("❌ Sessions caisse:", error);
      setSessionsError(error.message || "Erreur inconnue");
    } finally {
      setLoadingSessions(false);
    }
  }, [xStartDate, xEndDate, xCashierId]);

  const fetchLogoutAudits = useCallback(async () => {
    try {
      setLoadingLogoutAudits(true);
      setLogoutAuditsError(null);
      const paramsObject = {
        startDate: xStartDate,
        endDate: xEndDate,
      };
      if (xCashierId) {
        paramsObject.cashierId = xCashierId;
      }
      const params = new URLSearchParams(paramsObject);
      const response = await fetch(
        `${API_BASE}/reports/cash-logout-audits?${params}`,
      );
      if (!response.ok) {
        throw new Error("Impossible de charger le journal des déconnexions");
      }
      const data = await response.json();
      setLogoutAudits(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("❌ Journal déconnexions:", error);
      setLogoutAuditsError(error.message || "Erreur inconnue");
    } finally {
      setLoadingLogoutAudits(false);
    }
  }, [xStartDate, xEndDate, xCashierId]);

  const fetchZReport = useCallback(async () => {
    try {
      setLoadingZReport(true);
      setZReportError(null);
      const params = new URLSearchParams({
        startDate: xStartDate,
        endDate: xEndDate,
      });
      const response = await fetch(`${API_BASE}/reports/z-report?${params}`);
      if (!response.ok) {
        let detail = "";
        try { const err = await response.json(); detail = err?.details || err?.error || ""; } catch (_) {}
        throw new Error(`Impossible de charger le rapport Z${detail ? ` — ${detail}` : ` (HTTP ${response.status})`}`);
      }
      const data = await response.json();
      setZReport(data);
    } catch (error) {
      console.error("❌ Rapport Z:", error);
      setZReportError(error.message || "Erreur inconnue");
    } finally {
      setLoadingZReport(false);
    }
  }, [xStartDate, xEndDate]);

  useEffect(() => {
    fetchCashSessions();
  }, [fetchCashSessions]);

  useEffect(() => {
    fetchLogoutAudits();
  }, [fetchLogoutAudits]);

  useEffect(() => {
    fetchZReport();
  }, [fetchZReport]);

  const refreshAll = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([fetchCashSessions(), fetchLogoutAudits(), fetchZReport()]);
    } finally {
      setIsRefreshing(false);
    }
  };


  const printZReport = async () => {
    if (!zReport) return;
    try {
      const [settingsRes, infoRes] = await Promise.all([
        fetch("/api/receipt-printer-settings").catch(() => null),
        fetch("/api/restaurant-info").catch(() => null),
      ]);
      const settings = settingsRes?.ok ? await settingsRes.json() : null;
      const info = infoRes?.ok ? await infoRes.json() : null;
      const restaurantName = info?.name || "Restaurant";
      const html = buildZReportPrintHtml({
        report: zReport,
        restaurantName,
        printedBy: zPrintedBy,
      });

      const paperWidthMm = Number(settings?.paper_width_mm) === 58 ? 58 : 80;
      const rawPrinterName = String(settings?.printer_name || "").trim();
      const sanitizedDeviceName =
        rawPrinterName && rawPrinterName.toLowerCase() !== "ticket caisse"
          ? rawPrinterName
          : undefined;
      if (window?.electronAPI?.printHtml) {
        await window.electronAPI.printHtml({
          html,
          silent: true,
          paperWidthMm,
          deviceName: sanitizedDeviceName,
        });
        return;
      }
      const iframe = document.createElement("iframe");
      iframe.style.cssText =
        "position:fixed;left:-9999px;top:0;width:820px;height:1200px;border:0;opacity:0;pointer-events:none";
      iframe.setAttribute("aria-hidden", "true");
      iframe.onload = () => {
        iframe.contentWindow?.print();
        setTimeout(() => iframe.remove(), 1500);
      };
      iframe.srcdoc = html;
      document.body.appendChild(iframe);
    } catch (err) {
      console.error("Erreur impression rapport Z:", err);
    }
  };

  const xSessions = cashSessions?.xSessions || [];
  const xSessionsWithSales = xSessions.filter((session) => {
    // sales_total est recalculé depuis les commandes encore reportables.
    // total_transactions est un ancien cache qui peut inclure une vente annulée.
    const salesTotal = Number(session.sales_total ?? 0);
    const ordersCount = Number(session.orders_count || 0);
    return salesTotal > 0 || ordersCount > 0;
  });
  const reportUser = selectedXSession
    ? {
        first_name: selectedXSession.first_name,
        last_name: selectedXSession.last_name,
      }
    : currentUser;
  const zRange = zReport?.range || {};
  const zGross = zReport?.gross || {};
  const zNet = zReport?.net || {};
  const zModesGross = zReport?.modes?.gross || [];
  const zModesNet = zReport?.modes?.net || [];
  const zCategories = zReport?.categories || [];
  const zAdjustments = zReport?.adjustments || {};
  const zCashiers = zReport?.cashiers || [];
  const zInitiators = zReport?.initiators || zReport?.servers || [];
  const zUsesStewards = zInitiators.some(
    (person) => person?.attribution_kind === "steward",
  );
  const zServerSectionTitle = zUsesStewards ? "Serveurs" : "Commandes initiées par";
  const zPaymentMethods = zReport?.payment_methods || [];
  const zSalesSummary = zReport?.sales_summary || {};
  const zCashLife = zReport?.cash_life || {};
  const zCashPeriod = zCashLife.period || {};
  const zCashCounts = zCashLife.counts || {};
  const zHasActivity =
    Number(zGross?.tickets ?? zGross?.services ?? 0) > 0 ||
    Number(zNet?.total || 0) !== 0 ||
    Number(zReport?.reserves?.issuedMinor || 0) !== 0 ||
    Number(zReport?.reserves?.redeemedMinor || 0) !== 0 ||
    Number(zCashPeriod.opening_funds || 0) !== 0 ||
    Number(zCashPeriod.deposits || 0) !== 0 ||
    Number(zCashPeriod.withdrawals || 0) !== 0 ||
    Number(zCashCounts.count || 0) > 0;
  const zPrintedBy =
    `${currentUser?.first_name || ""} ${currentUser?.last_name || ""}`.trim() ||
    currentUser?.name ||
    currentUser?.username ||
    "—";

  const renderModeBreakdown = (modes = [], variant = "gross") => (
    <div className="space-y-2">
      {modes.map((mode) => (
        <div key={`${variant}-${mode.mode}`} className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm">
          <p className="font-semibold">{mode.mode}</p>
          <div className="mt-1 ml-3 space-y-0.5 text-xs opacity-70">
            <p>Montant : {formatCurrency(mode.total || 0)}</p>
            <p>Ticket(s) : {formatNumber(mode.services || 0)}</p>
          </div>
        </div>
      ))}
    </div>
  );

  const renderCategoryBreakdown = (items = []) => (
    <div className="space-y-2">
      {items.map((category) => (
        <div key={category.id} className="flex items-center justify-between text-sm">
          <span className="font-semibold">{category.name}</span>
          <span className="opacity-70">
            {formatCurrency(category.total || 0)} · {formatNumber(category.items_count || 0)} items
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <div
      className={`min-h-screen ${
        isDarkMode
          ? "bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100"
          : "bg-gradient-to-b from-slate-50 via-white to-slate-100 text-slate-900"
      }`}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-8 space-y-6 sm:space-y-8">
        <header
          className={`${cardBg(isDarkMode)} rounded-3xl p-4 sm:p-6 flex flex-wrap items-center justify-between gap-6 shadow-xl`}
        >
          <SectionTitle
            title="Rapports X & Z"
            subtitle="Suivi centralisé des sessions de caisse et rapports détaillés des caissiers."
          />
          <button
            type="button"
            onClick={refreshAll}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl border border-white/10 text-sm font-semibold hover:border-amber-400/60"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </header>

        <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg space-y-6`}>
          {sessionsError && (
            <div className="border border-rose-400/30 rounded-2xl p-4 bg-rose-500/10 text-sm text-rose-200">
              {sessionsError}
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Terminal className="w-5 h-5 text-amber-400" />
                  Rapports X (sessions de la période)
                </h3>
                <span className="text-xs uppercase tracking-[0.3em] opacity-70">
                  {xSessionsWithSales.length} sessions
                </span>
              </div>
              <div
                className={`rounded-2xl border p-4 flex flex-wrap items-end gap-4 ${
                  isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
                }`}
              >
                <div className="flex flex-col text-sm gap-2">
                  <span className="flex items-center gap-2 opacity-70">
                    <Calendar className="w-4 h-4" />
                    Début
                  </span>
                  <input
                    type="date"
                    value={xStartDate}
                    onChange={(e) => setXStartDate(e.target.value)}
                    className={`rounded-2xl border px-4 py-2 ${
                      isDarkMode ? "bg-white/5 border-white/10" : "bg-white border-slate-200"
                    }`}
                  />
                </div>
                <div className="flex flex-col text-sm gap-2">
                  <span className="flex items-center gap-2 opacity-70">
                    <Calendar className="w-4 h-4" />
                    Fin
                  </span>
                  <input
                    type="date"
                    value={xEndDate}
                    onChange={(e) => setXEndDate(e.target.value)}
                    className={`rounded-2xl border px-4 py-2 ${
                      isDarkMode ? "bg-white/5 border-white/10" : "bg-white border-slate-200"
                    }`}
                  />
                </div>
                <div className="flex flex-col text-sm gap-2 min-w-[220px]">
                  <span className="flex items-center gap-2 opacity-70">
                    <Users className="w-4 h-4" />
                    Caissier
                  </span>
                  <select
                    value={xCashierId}
                    onChange={(e) => setXCashierId(e.target.value)}
                    className={`rounded-2xl border px-4 py-2 ${
                      isDarkMode ? "bg-white/5 border-white/10" : "bg-white border-slate-200"
                    }`}
                  >
                    <option value="">Tous les caissiers</option>
                    {cashSessions.cashiers.map((cashier) => (
                      <option key={cashier.id} value={cashier.id}>
                        {cashier.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {loadingSessions ? (
                <SessionsEmptyState loading />
              ) : xSessionsWithSales.length === 0 ? (
                <SessionsEmptyState message="Aucune vente au cours de cette période." />
              ) : (
                <div className="space-y-4">
                  {xSessionsWithSales.map((session) => (
                    <XReportCard
                      key={session.id}
                      session={session}
                      isDarkMode={isDarkMode}
                      onViewReport={(target) => {
                        setSelectedXSession(target);
                        setShowXReport(true);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <UserCircle className="w-5 h-5 text-emerald-400" />
                  Rapport Z (synthèse de journée)
                </h3>
                <div className="flex items-center gap-3">
                  <span className="text-xs uppercase tracking-[0.3em] opacity-70">
                    {formatDateTime(zRange.start)} → {formatDateTime(zRange.end)}
                  </span>
                  {zHasActivity && (
                    <button
                      type="button"
                      onClick={printZReport}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border border-white/10 hover:border-amber-400/60 hover:bg-white/10"
                    >
                      <Printer className="w-3.5 h-3.5" />
                      Imprimer
                    </button>
                  )}
                </div>
              </div>

              {zReportError && (
                <div className="border border-rose-400/30 rounded-2xl p-4 bg-rose-500/10 text-sm text-rose-200">
                  {zReportError}
                </div>
              )}

              {loadingZReport ? (
                <div className="rounded-2xl border border-dashed border-white/20 p-6 text-center text-sm opacity-70">
                  Chargement du rapport Z...
                </div>
              ) : !zHasActivity ? (
                <SessionsEmptyState message="Aucune activité au cours de cette période." />
              ) : (
                <div className="space-y-5">
                  <div className={`${cardBg(isDarkMode)} rounded-3xl p-5 space-y-4`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">
                          Rapport Z
                        </p>
                        <p className="text-lg font-semibold">
                          Synthèse quotidienne de l'établissement
                        </p>
                      </div>
                      <div className="text-xs text-right space-y-1 opacity-70">
                        <div>Première vente: {formatDateTime(zReport.first_sale_at)}</div>
                        <div>Dernière vente: {formatDateTime(zReport.last_sale_at)}</div>
                        <div>Impression: {formatDateTime(zReport.generated_at)}</div>
                        <div>Tiré par: {zPrintedBy}</div>
                      </div>
                    </div>

                    <div>
                      <p className="text-xs uppercase tracking-[0.3em] text-amber-300 mb-3">
                        Chiffres bruts
                      </p>
                      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
                        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                          <p className="text-xs uppercase tracking-[0.3em] opacity-70">
                            CA brut global
                          </p>
                          <p className="text-xl font-semibold mt-2">
                            {formatCurrency(zGross.total || 0)}
                          </p>
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                          <p className="text-xs uppercase tracking-[0.3em] opacity-70">
                            Tickets
                          </p>
                          <p className="text-xl font-semibold mt-2">
                            {formatNumber(zGross.tickets ?? zGross.services ?? 0)}
                          </p>
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                          <p className="text-xs uppercase tracking-[0.3em] opacity-70">
                            Tables servies
                          </p>
                          <p className="text-xl font-semibold mt-2">
                            {formatNumber(zGross.tables_served || 0)}
                          </p>
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                          <p className="text-xs uppercase tracking-[0.3em] opacity-70">
                            Nombre de visiteurs
                          </p>
                          <p className="text-xl font-semibold mt-2">
                            {formatNumber(zGross.visitors || 0)}
                          </p>
                        </div>
                      </div>
                      {Number(zGross.tax_total || 0) > 0 && (
                        <div className="mt-3 rounded-2xl border border-white/10 bg-white/5 p-4 flex items-center justify-between">
                          <span className="text-xs uppercase tracking-[0.3em] opacity-70">TVA</span>
                          <span className="font-semibold">{formatCurrency(zGross.tax_total)}</span>
                        </div>
                      )}
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] opacity-60 mb-3">
                        <span>Détails par mode</span>
                        <span>CA / Tickets / Visiteurs</span>
                      </div>
                      {renderModeBreakdown(zModesGross, "gross")}
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] opacity-60 mb-3">
                        <span>Détails par catégories</span>
                        <span>CA / Articles</span>
                      </div>
                      {zCategories.length === 0 ? (
                        <p className="text-sm opacity-70">Aucune catégorie enregistrée.</p>
                      ) : (
                        renderCategoryBreakdown(zCategories)
                      )}
                    </div>
                  </div>

                  <div className={`${cardBg(isDarkMode)} rounded-3xl p-5 space-y-4`}>
                    <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                      Ajustements & contrôles
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                        <p className="text-xs uppercase tracking-[0.3em] opacity-70">
                          Annulations
                        </p>
                        <p className="text-lg font-semibold mt-2">
                          {formatNumber(zAdjustments.cancellations?.count || 0)} ·{" "}
                          {formatCurrency(zAdjustments.cancellations?.amount || 0)}
                        </p>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                        <p className="text-xs uppercase tracking-[0.3em] opacity-70">
                          Remises
                        </p>
                        <p className="text-lg font-semibold mt-2">
                          {formatNumber(zAdjustments.discounts?.count || 0)} ·{" "}
                          {formatCurrency(zAdjustments.discounts?.amount || 0)}
                        </p>
                        <p className="text-xs opacity-60 mt-1">
                          {Number(zAdjustments.discounts?.percent_of_gross || 0).toFixed(1)}% du brut
                        </p>
                      </div>
                    </div>

                  </div>

                  <div className={`${cardBg(isDarkMode)} rounded-3xl p-5 space-y-4`}>
                    <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                      Résumé ventes
                    </p>
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-2">
                      {[
                        ["Brut", formatCurrency(zSalesSummary.gross_sales ?? zGross.total)],
                        ["Remises", formatSignedCurrency(-Number(zSalesSummary.total_discounts ?? zAdjustments.discounts?.amount ?? 0))],
                        ["À crédit", formatSignedCurrency(-Number(zSalesSummary.total_credit_sales || 0))],
                        ["Réserves utilisées", formatSignedCurrency(-Number(zSalesSummary.reserve_redeemed || 0))],
                      ].map(([label, value]) => (
                        <div key={label} className="flex items-center justify-between gap-4 text-sm">
                          <span>{label}</span>
                          <span className="font-semibold whitespace-nowrap">{value}</span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between gap-4 border-t border-white/10 pt-3 text-lg font-semibold text-emerald-200">
                        <span>Net</span>
                        <span className="whitespace-nowrap">{formatCurrency(zSalesSummary.net_sales ?? zNet.total)}</span>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-3 gap-3">
                      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                        <p className="text-xs uppercase tracking-[0.3em] opacity-70">Tickets</p>
                        <p className="text-2xl font-semibold mt-2">{formatNumber(zNet.ticket_count ?? zGross.tickets ?? zGross.services ?? 0)}</p>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                        <p className="text-xs uppercase tracking-[0.3em] opacity-70">Panier moyen</p>
                        <p className="text-2xl font-semibold mt-2">{formatCurrency(zNet.average_ticket || 0)}</p>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                        <p className="text-xs uppercase tracking-[0.3em] opacity-70">Moyenne par visiteur</p>
                        <p className="text-2xl font-semibold mt-2">
                          {zNet.average_per_visitor == null ? "—" : formatCurrency(zNet.average_per_visitor)}
                        </p>
                      </div>
                    </div>

                    <ReserveReportSummary data={zReport?.reserves} />
                    {zPaymentMethods.length > 0 && (
                      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                        <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] opacity-60 mb-3">
                          <span>Par mode de paiement</span>
                          <span>Montant / %</span>
                        </div>
                        <div className="space-y-2">
                          {zPaymentMethods.map((pm) => (
                            <div key={pm.name} className="flex items-center justify-between text-sm">
                              <span className="font-semibold">{pm.name}</span>
                              <span className="opacity-70">
                                {formatCurrency(pm.amount || 0)}
                                <span className="ml-2 text-xs opacity-60">{Number(pm.percentage || 0).toFixed(1)}%</span>
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] opacity-60 mb-3">
                        <span>CA net par mode</span>
                        <span>Montant / Tickets</span>
                      </div>
                      {renderModeBreakdown(zModesNet, "net")}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3 text-sm">
                      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                        <p className="text-xs uppercase tracking-[0.3em] opacity-70">
                          CA par caissier
                        </p>
                        <p className="text-lg font-semibold mt-2">
                          {formatNumber(zCashiers.length)} caissier(s)
                        </p>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                        <p className="text-xs uppercase tracking-[0.3em] opacity-70">
                          {zServerSectionTitle}
                        </p>
                        <p className="text-lg font-semibold mt-2">
                          {formatNumber(zInitiators.length)} membre(s) de l'équipe
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className={`${cardBg(isDarkMode)} rounded-3xl p-5 space-y-4`}>
                    <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                      Vie de caisse
                    </p>
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-2">
                      {[
                        ["Fonds initiaux", formatCurrency(zCashPeriod.opening_funds || 0)],
                        ["Dépôts", formatSignedCurrency(zCashPeriod.deposits || 0)],
                        ["Retraits", formatSignedCurrency(-Number(zCashPeriod.withdrawals || 0))],
                        ["Espèces encaissées", formatCurrency(zCashPeriod.cash_collected || 0)],
                      ].map(([label, value]) => (
                        <div key={label} className="flex items-center justify-between gap-4 text-sm">
                          <span>{label}</span>
                          <span className="font-semibold whitespace-nowrap">{value}</span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between gap-4 border-t border-white/10 pt-3 font-semibold">
                        <span>Solde théorique</span>
                        <span className="whitespace-nowrap">{formatCurrency(zCashPeriod.net_cash_flow || 0)}</span>
                      </div>
                    </div>
                    {Number(zCashCounts.count || 0) > 0 && (
                      <div className="grid sm:grid-cols-3 gap-3 text-sm">
                        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                          <p className="opacity-70">Solde attendu</p>
                          <p className="mt-1 font-semibold">{formatCurrency(zCashCounts.expected || 0)}</p>
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                          <p className="opacity-70">Comptage réel</p>
                          <p className="mt-1 font-semibold">{formatCurrency(zCashCounts.actual || 0)}</p>
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                          <p className="opacity-70">Écart</p>
                          <p className="mt-1 font-semibold">{formatSignedCurrency(zCashCounts.difference || 0)}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className={`${cardBg(isDarkMode)} rounded-3xl p-5 space-y-5`}>
                    <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                      Encaissement & performances humaines
                    </p>

                    <div className="space-y-3">
                      <p className="text-sm font-semibold">CA par caissier</p>
                      {zCashiers.length === 0 ? (
                        <p className="text-sm opacity-70">Aucun caissier sur la période.</p>
                      ) : (
                        zCashiers.map((cashier) => (
                          <div
                            key={`cashier-${cashier.id}`}
                            className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-3"
                          >
                            <p className="font-semibold">{cashier.name}</p>
                            <div className="ml-3 space-y-1 text-sm opacity-80">
                              <p>Montant : {formatCurrency(cashier.net_total || 0)}</p>
                              <p>Ticket(s) : {formatNumber(cashier.services || 0)}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="space-y-3">
                      <p className="text-sm font-semibold">{zServerSectionTitle}</p>
                      {zInitiators.length === 0 ? (
                        <p className="text-sm opacity-70">Aucun initiateur sur la période.</p>
                      ) : (
                        zInitiators.map((server) => (
                          <div
                            key={`server-${server.attribution_key || server.id}`}
                            className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-3"
                          >
                            <p className="font-semibold">
                              {server.name} · {formatReportRole(server.role)}
                            </p>
                            <div className="ml-3 text-sm opacity-80">
                              <p>Montant : {formatCurrency(server.net_total || 0)}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                  </div>

                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-400" />
                Journal des déconnexions
              </h3>
              <span className="text-xs uppercase tracking-[0.3em] opacity-70">
                {logoutAudits.length} entrées
              </span>
            </div>

            {logoutAuditsError && (
              <div className="border border-rose-400/30 rounded-2xl p-4 bg-rose-500/10 text-sm text-rose-200">
                {logoutAuditsError}
              </div>
            )}

            <div
              className={`rounded-2xl border p-4 space-y-4 ${
                isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
              }`}
            >
              {loadingLogoutAudits ? (
                <p className="text-sm opacity-70">Chargement des journaux...</p>
              ) : logoutAudits.length === 0 ? (
                <p className="text-sm opacity-70">
                  Aucune déconnexion enregistrée pour cette période.
                </p>
              ) : (
                logoutAudits.map((audit) => {
                  const methodMap = new Map();
                  const mergeTotals = (items, key) => {
                    if (!Array.isArray(items)) return;
                    items.forEach((item) => {
                      const name = String(item?.name || "Autre").trim() || "Autre";
                      const normalized = name.toLowerCase();
                      const entry =
                        methodMap.get(normalized) || {
                          name,
                          expected: 0,
                          actual: 0,
                          diff: 0,
                        };
                      entry[key] = Number(item?.amount) || 0;
                      methodMap.set(normalized, entry);
                    });
                  };
                  mergeTotals(audit.expected_totals, "expected");
                  mergeTotals(audit.actual_totals, "actual");
                  mergeTotals(audit.difference_totals, "diff");
                  const methods = Array.from(methodMap.values());
                  const totalDiff = Number(audit.total_difference || 0);
                  const totalDiffClass =
                    Math.abs(totalDiff) <= 0.01
                      ? isDarkMode
                        ? "text-emerald-200"
                        : "text-emerald-600"
                      : isDarkMode
                        ? "text-rose-200"
                        : "text-rose-600";
                  const isForced = Boolean(audit.is_forced);
                  const statusLabel = isForced
                    ? "Déconnexion forcée"
                    : "Déconnexion validée";
                  const statusClass = isForced
                    ? isDarkMode
                      ? "bg-rose-500/20 text-rose-200"
                      : "bg-rose-100 text-rose-600"
                    : isDarkMode
                      ? "bg-emerald-400/15 text-emerald-200"
                      : "bg-emerald-100 text-emerald-600";
                  const adjustments = audit.adjustments || {};
                  const hasAdjustments =
                    Number(adjustments.total_discounts || 0) !== 0 ||
                    Number(adjustments.cash_in || 0) !== 0 ||
                    Number(adjustments.cash_out || 0) !== 0 ||
                    Number(adjustments.cancelled_items || 0) !== 0;

                  return (
                    <div
                      key={audit.id}
                      className={`rounded-2xl border p-4 ${
                        isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
                      }`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                          <p className="text-xs uppercase tracking-[0.3em] opacity-60">
                            Déconnexion
                          </p>
                          <p className="text-lg font-semibold">
                            {audit.cashier_name ||
                              (audit.cashier_id ? `Caissier #${audit.cashier_id}` : "Caissier")}
                          </p>
                          <p className="text-xs opacity-70">
                            {formatDateTime(audit.created_at)}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className={`text-xs rounded-full px-3 py-1 ${statusClass}`}>
                            {statusLabel}
                          </span>
                          <div className="text-right text-sm">
                            <p className="opacity-70">Écart total</p>
                            <p className={`font-semibold ${totalDiffClass}`}>
                              {formatSignedCurrency(totalDiff)}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 grid gap-4 md:grid-cols-2">
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="opacity-70">Total théorique</span>
                            <span className="font-semibold">
                              {formatCurrency(audit.total_expected)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="opacity-70">Total saisi</span>
                            <span className="font-semibold">
                              {formatCurrency(audit.total_actual)}
                            </span>
                          </div>
                          {isForced && (
                            <div className="text-xs opacity-70">
                              Validé par{" "}
                              <span className="font-semibold">
                                {audit.forced_by_name || "Manager"}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between text-xs uppercase opacity-60">
                            <span>Mode</span>
                            <span>Théorique / Saisi / Écart</span>
                          </div>
                          {methods.length === 0 ? (
                            <p className="text-sm opacity-70">Aucun détail par mode.</p>
                          ) : (
                            methods.map((method) => {
                              const diffClass =
                                Math.abs(method.diff || 0) <= 0.01
                                  ? isDarkMode
                                    ? "text-emerald-200"
                                    : "text-emerald-600"
                                  : isDarkMode
                                    ? "text-rose-200"
                                    : "text-rose-600";
                              return (
                                <div
                                  key={`${audit.id}-${method.name}`}
                                  className="flex items-center justify-between gap-3"
                                >
                                  <span className="font-semibold">{method.name}</span>
                                  <span className="text-xs opacity-70">
                                    {formatCurrency(method.expected)} · {formatCurrency(method.actual)} ·{" "}
                                    <span className={`font-semibold ${diffClass}`}>
                                      {formatSignedCurrency(method.diff)}
                                    </span>
                                  </span>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>

                      {isForced && audit.forced_reason && (
                        <div
                          className={`mt-4 rounded-xl px-3 py-2 text-xs ${
                            isDarkMode ? "bg-white/5 text-white/70" : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          Motif: {audit.forced_reason}
                        </div>
                      )}

                      {hasAdjustments && (
                        <div className="mt-3 text-xs opacity-70">
                          Ajustements: remises {formatCurrency(adjustments.total_discounts || 0)} · annulations{" "}
                          {Number(adjustments.cancelled_items || 0)} article(s) · entrées caisse{" "}
                          {formatCurrency(adjustments.cash_in || 0)} · sorties caisse{" "}
                          {formatCurrency(adjustments.cash_out || 0)}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </section>
      </div>

      {showXReport && selectedXSession && (
        <XReportModal
          isOpen={showXReport}
          onClose={() => {
            setShowXReport(false);
            setSelectedXSession(null);
          }}
          currentUser={reportUser}
          currentSession={selectedXSession}
        />
      )}
    </div>
  );
};

const SectionTitle = ({ title, subtitle }) => (
  <div>
    <p className="text-xs uppercase tracking-[0.35em] text-amber-300">Rapports de caisse</p>
    <h2 className="text-2xl font-semibold mt-2">{title}</h2>
    <p className="text-sm opacity-70">{subtitle}</p>
  </div>
);

const SessionsEmptyState = ({ loading = false, message = "Aucune session disponible." }) => (
  <div className="rounded-2xl border border-dashed border-white/20 p-6 text-center text-sm opacity-70">
    {loading ? "Chargement des sessions…" : message}
  </div>
);

const XReportCard = ({ session, isDarkMode, onViewReport }) => {
  const isOpen = session.status === "open";
  const badgeClass = isOpen
    ? "bg-amber-400/20 text-amber-100"
    : "bg-emerald-400/15 text-emerald-200";

  return (
    <div
      className={`rounded-2xl border p-5 flex flex-col gap-3 ${
        isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Session #{session.id}</p>
          <p className="text-lg font-semibold">
            {session.first_name} {session.last_name}
          </p>
          <p className="text-xs opacity-70">{formatDateTime(session.session_start_time)}</p>
          {session.session_end_time && (
            <p className="text-xs opacity-50">
              Clôturée: {formatDateTime(session.session_end_time)}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`text-xs rounded-full px-3 py-1 ${badgeClass}`}>
            {session.status_label}
          </span>
          {onViewReport && (
            <button
              type="button"
              onClick={() => onViewReport(session)}
              className="text-xs font-semibold px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10"
            >
              {isOpen ? "Rapport X instantané" : "Voir rapport X"}
            </button>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 opacity-60" />
          Initial: {formatCurrency(session.initial_amount)}
        </div>
        <div className="flex items-center gap-2">
          <Receipt className="w-4 h-4 opacity-60" />
          Ventes: {formatCurrency(session.sales_total ?? 0)}
        </div>
      </div>
    </div>
  );
};

export default CashReports;
